// Pixel art do Agzos Office.
//
// Tudo aqui é DADO: listas de strings, um caractere por pixel. Nada de imagem
// binária — o projeto inteiro fica em texto, versionável e leve.
//
// Cada personagem tem o mesmo desenho (10x14) e uma paleta própria (cabelo, pele,
// roupa). Assim o escritório parece coeso e cada agente é reconhecível pela cor.

/** Um sprite: linhas de mesma largura, um caractere por pixel. */
export type Sprite = readonly string[];

/** Paleta: índice do caractere -> cor. */
export type Palette = Record<string, string>;

export const CHARACTER: Sprite = [
  '....1111....',
  '...111111...',
  '..11111111..',
  '..11111111..',
  '...111111...',
  '....2222....',
  '...222222...',
  '..22222222..',
  '..22222222..',
  '..33333333..',
  '..33333333..',
  '..33333333..',
  '..33....33..',
  '..44....44..',
  '..44....44..',
  '............',
];

export const DESK: Sprite = [
  '55555555555555555555',
  '56666666666666666665',
  '56666666666666666665',
  '56666666666666666665',
  '56666666666666666665',
  '56666666666666666665',
  '55555555555555555555',
  '57777777777777777775',
  '57777777777777777775',
  '57777777777777777775',
];

export const CHAIR: Sprite = [
  '..8888..',
  '.888888.',
  '.888888.',
  '.888888.',
  '..8888..',
  '...88...',
  '..8888..',
];

export const MONITOR: Sprite = [
  '..999999..',
  '.99aaaa99.',
  '9aabbbbaa9',
  '9aabbbbaa9',
  '9aabbbbaa9',
  '9aabbbbaa9',
  '.99aaaa99.',
  '..999999..',
  '...9999...',
  '..999999..',
];

export const PLANT: Sprite = [
  '..cc.cc..',
  '.c.cc.cc.',
  'c.cc..cc.',
  '.c.cc.cc.',
  '..cc.cc..',
  '...ccc...',
  '..cdddc..',
  '..cdddc..',
  '..cdddc..',
  '...cdc...',
];

export const WINDOW: Sprite = [
  'eeeeeeeeeeeeeeee',
  'effffffffffffffe',
  'efeeeeeefeeeeeee',
  'efeeeeeefeeeeeee',
  'effffffffffffffe',
  'effffffffffffffe',
  'efeeeeeefeeeeeee',
  'efeeeeeefeeeeeee',
  'effffffffffffffe',
  'effffffffffffffe',
  'eeeeeeeeeeeeeeee',
];

export const DOOR: Sprite = [
  'gggggggggggg',
  'g1111111111g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'g1222222221g',
  'gggggggggggg',
];

export const RUG: Sprite = [
  '........................',
  '..aaaaaaaaaaaaaaaaaaaa..',
  '.abbbbbbbbbbbbbbbbbbbba.',
  '.abbbbbbbbbbbbbbbbbbbba.',
  '..aaaaaaaaaaaaaaaaaaaa..',
  '........................',
];

export const LAMP: Sprite = [
  '..hhhh..',
  '.hhhhhh.',
  'hhhhhhhh',
  'hhhhhhhh',
  '..iiii..',
  '..iiii..',
  '..iiii..',
  '.jjjjjj.',
];

/** Cores do escritório (identidade Agzos: preto, vermelho, branco). */
export const OFFICE_COLORS = {
  floorA: '#22262f',
  floorB: '#272c36',
  wall: '#171a21',
  wallTop: '#1f232c',
  trim: '#3a4150',
  rug: '#4a2026',
  rugInner: '#6b2f36',
  glass: '#7fb3d5',
  plant: '#3f9c5a',
  plantDark: '#2c7040',
  pot: '#9c5a3a',
  deskTop: '#8a5f3c',
  desk: '#6b4429',
  deskDark: '#4d3020',
  chair: '#39404f',
  monitor: '#20242e',
  monitorLight: '#4aa8e8',
  screen: '#0d2a3f',
  skin: '#f2c9a0',
  hairDark: '#241a12',
  lamp: '#f6d774',
  lampBase: '#4a4f5c',
  door: '#7a4a2b',
  doorPanel: '#8f5b36',
  frame: '#333944',
} as const;

/** As quatro peles de personagem (cor de cabelo, pele e roupa). */
export const SKINS: readonly Palette[] = [
  { '1': '#241a12', '2': '#f2c9a0', '3': '#e03131', '4': '#2b2f3a' }, // ruivo? não: moreno, camisa vermelha
  { '1': '#6b4423', '2': '#d9a066', '3': '#4aa8e8', '4': '#2b2f3a' },
  { '1': '#e8e2d4', '2': '#f6d7b0', '3': '#5cc97b', '4': '#2b2f3a' },
  { '1': '#1f1f26', '2': '#8d5524', '3': '#a77bf3', '4': '#2b2f3a' },
];

/** Cor do badge de cada ferramenta. */
export const PROVIDER_COLORS: Record<string, string> = {
  mcode: '#e03131',
  claude: '#d97757',
  codex: '#10a37f',
  opencode: '#f5a524',
  antigravity: '#4285f4',
};

/** Sigla que aparece no badge do personagem. */
export const PROVIDER_LABELS: Record<string, string> = {
  mcode: 'MINIMAX',
  claude: 'CLAUDE',
  codex: 'CODEX',
  opencode: 'OPENCODE',
  antigravity: 'ANTIGRAVITY',
};

/** Sigla curta que aparece na tag em cima da cabeça do personagem. */
export const PROVIDER_TAGS: Record<string, string> = {
  mcode: 'MM',
  claude: 'CL',
  codex: 'CX',
  opencode: 'OC',
  antigravity: 'AG',
};

/** Paleta de um objeto do escritório (a letra é o índice do sprite). */
export const PROP_PALETTES: Record<string, Palette> = {
  DESK: { '5': OFFICE_COLORS.deskDark, '6': OFFICE_COLORS.deskTop, '7': OFFICE_COLORS.desk },
  CHAIR: { '8': OFFICE_COLORS.chair },
  MONITOR: { '9': OFFICE_COLORS.monitor, a: OFFICE_COLORS.screen, b: OFFICE_COLORS.monitorLight },
  PLANT: { c: OFFICE_COLORS.plant, d: OFFICE_COLORS.pot },
  WINDOW: { e: OFFICE_COLORS.frame, f: OFFICE_COLORS.glass },
  DOOR: { g: OFFICE_COLORS.frame, '1': OFFICE_COLORS.door, '2': OFFICE_COLORS.doorPanel },
  RUG: { a: OFFICE_COLORS.rug, b: OFFICE_COLORS.rugInner },
  LAMP: { h: OFFICE_COLORS.lamp, i: OFFICE_COLORS.lampBase, j: OFFICE_COLORS.lampBase },
};

/** Desenha um sprite no canvas. (x, y) é o canto superior esquerdo, em pixels lógicos. */
export function drawSprite(
  ctx: CanvasRenderingContext2D,
  sprite: Sprite,
  palette: Palette,
  x: number,
  y: number,
  scale: number,
): void {
  for (let row = 0; row < sprite.length; row++) {
    const line = sprite[row] ?? '';
    for (let col = 0; col < line.length; col++) {
      const key = line[col];
      if (!key || key === '.') continue;
      const color = palette[key];
      if (!color) continue;
      ctx.fillStyle = color;
      ctx.fillRect((x + col) * scale, (y + row) * scale, scale, scale);
    }
  }
}

/** Largura de um sprite, em pixels lógicos. */
export function spriteWidth(sprite: Sprite): number {
  return sprite.reduce((max, line) => Math.max(max, line.length), 0);
}
